import { useCallback, useEffect, useRef, useState } from 'react';

import { createNexusApi } from '../../../../domain/nexus/api';
import type { NexusDeliveredTasksResult } from '../../../../domain/nexus/model';
import { firebaseNexusGateway } from '../../../../platform/firebase/nexusGateway';
import { logGlobalError } from '../../../../utils/globalErrorLogger';
import { DELIVERED_TASKS_MAX_LIMIT, DELIVERED_TASKS_PAGE_SIZE } from './deliveredTaskHelpers';
import { diagnosticMessage } from './helpers';

const nexusApi = createNexusApi(firebaseNexusGateway);
const SEARCH_DEBOUNCE_MS = 300;

export interface DeliveredTasksViewModel {
  busy: boolean;
  loading: boolean;
  error: string | null;
  query: string;
  result: NexusDeliveredTasksResult | null;
  canLoadMore: boolean;
  initialize: () => Promise<void>;
  setQuery: (query: string) => void;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  clearError: () => void;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return diagnosticMessage(error);
}

export function useDeliveredTasksViewModel(initialQuery = ''): DeliveredTasksViewModel {
  const [busy, setBusy] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQueryState] = useState(initialQuery);
  const [limit, setLimit] = useState(DELIVERED_TASKS_PAGE_SIZE);
  const [result, setResult] = useState<NexusDeliveredTasksResult | null>(null);
  // Only the newest request may update state; typing fires overlapping searches.
  const requestSeq = useRef(0);
  const debounce = useRef<number | null>(null);

  const load = useCallback(async (nextQuery: string, nextLimit: number, context: string) => {
    const seq = ++requestSeq.current;
    // An empty search shows the bug drill-down instead; drop any pending result.
    if (!nextQuery.trim()) {
      setResult(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const next = await nexusApi.fetchDeliveredTasks({ query: nextQuery, limit: nextLimit });
      if (seq !== requestSeq.current) return;
      setResult(next);
      setError(null);
    } catch (nextError) {
      if (seq !== requestSeq.current) return;
      setError(errorMessage(nextError));
      logGlobalError(nextError, { context: `GoogleNexusTool.deliveredTasks.${context}`, metadata: { query: nextQuery, limit: nextLimit } });
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  const initialize = useCallback(async () => {
    setBusy(true);
    try {
      await load(initialQuery, DELIVERED_TASKS_PAGE_SIZE, 'initialize');
    } finally {
      setBusy(false);
    }
  }, [initialQuery, load]);

  const setQuery = useCallback((next: string) => {
    setQueryState(next);
    setLimit(DELIVERED_TASKS_PAGE_SIZE);
    if (debounce.current) window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => {
      debounce.current = null;
      void load(next, DELIVERED_TASKS_PAGE_SIZE, 'search');
    }, SEARCH_DEBOUNCE_MS);
  }, [load]);

  useEffect(() => () => {
    if (debounce.current) window.clearTimeout(debounce.current);
  }, []);

  const refresh = useCallback(() => load(query, limit, 'refresh'), [load, query, limit]);

  const loadMore = useCallback(async () => {
    const nextLimit = Math.min(limit + DELIVERED_TASKS_PAGE_SIZE, DELIVERED_TASKS_MAX_LIMIT);
    setLimit(nextLimit);
    await load(query, nextLimit, 'loadMore');
  }, [limit, load, query]);

  const clearError = useCallback(() => setError(null), []);

  return {
    busy,
    loading,
    error,
    query,
    result,
    canLoadMore: Boolean(result?.hasMore) && limit < DELIVERED_TASKS_MAX_LIMIT,
    initialize,
    setQuery,
    refresh,
    loadMore,
    clearError,
  };
}
